// The area-report write both area surfaces share — the drawer's board
// (`useTaskLocationsPersist`) and the Progress Tracker's cells
// (`useTrackerAreaWrite`). They ride the SAME immediate-write channel per task,
// so a task's reports are serialized and merged whichever surface made them:
// the server derives the task % from the stored one (QA R-04), and two reports
// in flight at once would each derive from the same base and one would lose.

import { setTaskAreaStatuses } from '@apis/schedule';
import type { IAreaStatusesResult } from '@d-orca/shared/client/apis';
import type { SiteId, TaskId, WritableLocationStatus } from '@interfaces/IScheduler';

/** The channel kind a task's area reports ride (`immediateWriteKey`). */
export const AREA_REPORTS_CHANNEL = 'areas';

/** The area reports waiting on a task's channel: the status per area (a later
 *  report for an area replaces an earlier one), and whether the session holds
 *  the % — a value set by hand that the areas must not move. */
export interface IAreaReportWrite {
  statuses: Record<string, WritableLocationStatus>;
  holdProgress: boolean;
}

/** `locationIds` reported as `status`. */
export function areaReport(
  locationIds: readonly string[],
  status: WritableLocationStatus,
  holdProgress: boolean,
): IAreaReportWrite {
  return { statuses: Object.fromEntries(locationIds.map((id) => [id, status])), holdProgress };
}

/** Fold a newer report into a pending one: its statuses win per area, and its
 *  hold is the session's current word. */
export function mergeAreaReports(prev: IAreaReportWrite, next: IAreaReportWrite): IAreaReportWrite {
  return { statuses: { ...prev.statuses, ...next.statuses }, holdProgress: next.holdProgress };
}

/** Send a report as ONE request; resolves to the % the server stored. */
export function sendAreaReport(
  ctx: { siteId: SiteId; scheduleNumber: string; taskId: TaskId },
  report: IAreaReportWrite,
): Promise<IAreaStatusesResult> {
  const changes = Object.entries(report.statuses).map(([locationId, status]) => ({ locationId, status }));
  return setTaskAreaStatuses(ctx.siteId, ctx.scheduleNumber, ctx.taskId, changes, { holdProgress: report.holdProgress });
}
